import React from 'react';
import { formatMarketMoney } from '../../../utils/eventPricingDisplay';
import EventLoadingState from './EventLoadingState';

export const money = (n, pricingMarket) => formatMarketMoney(n, pricingMarket || 'LOCAL');

export const bytesToLabel = (size) => {
  const v = Number(size || 0);
  if (!v) return '';
  if (v >= 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(2)} MB`;
  return `${Math.ceil(v / 1024)} KB`;
};

export const defaultPaymentTerms = [
  'Payment can be made via Cash, Cheque, or Bank Transfer.',
  'Full payment must be completed on or before the stated payment deadline.',
  'Payments made after the deadline may result in delays or non-confirmation of the application.',
  'All payments once made are non-refundable and non-transferable after the Tax Invoice is issued.',
  'If payment is made via bank transfer, a clear copy of the payment receipt/slip must be shared with the organizing team for verification.',
  'Applications will be confirmed only after successful payment verification.',
  'Any bank charges or transfer fees must be borne by the applicant.',
  'Invoice details cannot be changed after the Tax Invoice has been issued.',
  'SLIM reserves the right to cancel or reject applications that do not comply with the payment requirements.',
  'Applicants should keep a copy of the payment proof for future reference.',
];

const BANK_ROW_KEYS = [
  'beneficiaryName',
  'beneficiaryBank',
  'bankCode',
  'beneficiaryBranch',
  'branchCode',
  'accountNumber',
];

const bankTransferRowComplete = (d) =>
  d && BANK_ROW_KEYS.every((k) => String(d[k] || '').trim());

export const EventPaymentInstructions = ({ paymentDisplay, bankAccount, showCardSettlementNote = true }) => {
  const pd = paymentDisplay || {};
  const d = pd.bankTransferDetails || {};
  const leg = pd.ledgerBank || {};
  const card = pd.cardClearingAccount || {};
  const ledgerLine = [bankAccount?.code, bankAccount?.name].filter(Boolean).join(' – ')
    || [leg.code, leg.name].filter(Boolean).join(' – ');

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 sm:px-5 py-3 border-b border-gray-200">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">Payment instructions</h2>
        <p className="mt-1 text-xs text-gray-600">
          Use these details for bank transfer or cheque. Card payments use the button below.
        </p>
      </div>
      <div className="px-4 sm:px-5 py-4 space-y-4 text-sm">
        {pd.chequePayeeName ? (
          <p className="text-gray-800">
            <span className="font-medium text-gray-900">Cheques:</span> Payable to{' '}
            <span className="font-semibold">{pd.chequePayeeName}</span>
          </p>
        ) : null}

        {bankTransferRowComplete(d) ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border border-gray-200 rounded-md overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 border-b">Beneficiary</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 border-b">Bank</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 border-b">Codes</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 border-b">Account</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-2 py-2 text-gray-800 border-b border-gray-100 align-top">
                    <div>{d.beneficiaryName}</div>
                    <div className="text-gray-500">{d.beneficiaryBranch}</div>
                  </td>
                  <td className="px-2 py-2 text-gray-800 border-b border-gray-100 align-top">{d.beneficiaryBank}</td>
                  <td className="px-2 py-2 text-gray-800 border-b border-gray-100 align-top">
                    <div>Bank: {d.bankCode}</div>
                    <div>Branch: {d.branchCode}</div>
                  </td>
                  <td className="px-2 py-2 text-gray-900 font-mono border-b border-gray-100 align-top">{d.accountNumber}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : ledgerLine ? (
          <p className="text-gray-700">
            <span className="font-medium text-gray-900">Ledger bank / cash:</span> {ledgerLine}
          </p>
        ) : (
          <p className="text-amber-800 text-sm">Bank transfer details are not configured. Please contact the event team.</p>
        )}

        {showCardSettlementNote && (card.code || card.name) && (
          <p className="text-gray-700">
            <span className="font-medium text-gray-900">Card payments:</span> Settlements are recorded against{' '}
            {[card.code, card.name].filter(Boolean).join(' – ')}.
          </p>
        )}
        {pd.cardPaymentNote ? (
          <p className="text-gray-700 whitespace-pre-wrap border-t border-gray-100 pt-3">{pd.cardPaymentNote}</p>
        ) : null}
      </div>
    </div>
  );
};

export const statusPillClass = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PAID') return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (s === 'PENDING_APPROVAL') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (s === 'ACTIVE') return 'bg-indigo-50 text-indigo-800 border-indigo-200';
  if (s === 'REJECTED') return 'bg-red-50 text-red-800 border-red-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
};

export const PAYMENT_LINK_DISPLAY = {
  NOT_REQUESTED: 'Not requested',
  ACTIVE: 'Active',
  PAID: 'Paid',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
};

export function isOfflinePublicPaymentMethod(method) {
  const m = String(method || '').toUpperCase();
  return m === 'BANK_TRANSFER' || m === 'CHEQUE_DEPOSIT';
}

export function formatPublicPaymentStatusPillLabel(data) {
  const paySt = String(data?.paymentStatus || 'UNPAID').toUpperCase();
  const method = String(data?.paymentMethod || '').toUpperCase();
  if (paySt === 'PENDING_APPROVAL' && isOfflinePublicPaymentMethod(method)) return 'Hold';
  const map = {
    PENDING_APPROVAL: 'Pending approval',
    PAID: 'Paid',
    UNPAID: 'Unpaid',
    REJECTED: 'Rejected',
    REFUNDED: 'Refunded',
    PARTIALLY_PAID: 'Partially paid',
  };
  return map[paySt] || String(data?.paymentStatus || 'Unpaid');
}

export function formatPublicPaymentLinkPillLabel(linkStatus) {
  const u = String(linkStatus || 'NOT_REQUESTED').toUpperCase();
  return PAYMENT_LINK_DISPLAY[u] || String(linkStatus || 'Not requested');
}

export const formatPaymentMethodLabel = (m) => {
  const v = String(m || '').toUpperCase();
  if (v === 'CARD') return 'Card payment (Paycorp)';
  if (v === 'BANK_TRANSFER') return 'Bank transfer';
  if (v === 'CHEQUE_DEPOSIT') return 'Cheque deposit';
  return m || 'Offline payment';
};

export const formatPaymentDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

export const SubmittedDetailRow = ({ label, value, mono = false }) => (
  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 text-sm">
    <dt className="col-span-1 text-gray-600">{label}</dt>
    <dd className={`col-span-2 sm:col-span-3 text-gray-900 ${mono ? 'font-mono break-all' : ''}`}>
      {value || '—'}
    </dd>
  </div>
);

export const PaymentConfirmedCard = ({
  data,
  onDownloadTaxInvoice,
  onDownloadBillingInvoice,
  onDownloadReceipt,
  amountField = 'paymentAmount',
  invoiceTotalField = 'invoice',
  confirmedDescription = 'Your payment has been received and your applications are now confirmed for this event.',
}) => (
  <div className="bg-white border-l-4 border-emerald-500 border border-emerald-200 rounded-xl shadow-sm p-4 sm:p-5">
    <div className="flex items-start gap-3">
      <div className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-emerald-100 text-emerald-700">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="text-base sm:text-lg font-semibold text-emerald-900">Payment confirmed</h2>
        <p className="mt-1 text-sm text-emerald-900/80">{confirmedDescription}</p>
        <dl className="mt-4 space-y-1.5">
          <SubmittedDetailRow label="Method" value={formatPaymentMethodLabel(data?.paymentMethod)} />
          <SubmittedDetailRow
            label="Amount"
            value={money(
              data?.[amountField] || data?.[invoiceTotalField]?.grandTotal || data?.taxInvoice?.grandTotal,
              data?.pricingMarket
            )}
          />
          <SubmittedDetailRow label="Paid on" value={formatPaymentDate(data?.paidAt)} />
          {data?.paymentReferenceNumber ? (
            <SubmittedDetailRow label="Reference" value={data.paymentReferenceNumber} mono />
          ) : null}
        </dl>
        <div className="mt-4 flex flex-wrap gap-2">
          {onDownloadReceipt && (
            <button
              type="button"
              onClick={onDownloadReceipt}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-violet-600 text-white text-sm font-medium hover:bg-violet-700"
            >
              Download Payment Receipt
            </button>
          )}
          {data?.taxInvoice?.invoiceNumber && onDownloadTaxInvoice && (
            <button
              type="button"
              onClick={onDownloadTaxInvoice}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
            >
              Download Tax Invoice ({data.taxInvoice.invoiceNumber})
            </button>
          )}
          {data?.billingInvoice?.invoiceNumber && onDownloadBillingInvoice && (
            <button
              type="button"
              onClick={onDownloadBillingInvoice}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-sky-600 text-white text-sm font-medium hover:bg-sky-700"
            >
              Download Invoice ({data.billingInvoice.invoiceNumber})
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
);

export const PaymentPendingApprovalCard = ({ data, contactEmail }) => {
  const receipts = Array.isArray(data?.receiptDocuments) ? data.receiptDocuments : [];
  const email = contactEmail || data?.coordinatorEmail || data?.contactEmail || 'your registered address';
  return (
    <div className="bg-amber-50 border-l-4 border-amber-500 border border-amber-200 rounded-xl shadow-sm p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-100 text-amber-700">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base sm:text-lg font-semibold text-amber-900">Payment submitted — awaiting approval</h2>
          <p className="mt-1 text-sm text-amber-900/90">
            We&apos;ve received your payment details. Our team will verify them and approve the payment shortly.
            You&apos;ll get a confirmation email at <span className="font-semibold">{email}</span> once it&apos;s approved.
          </p>
          <dl className="mt-4 space-y-1.5">
            <SubmittedDetailRow label="Method" value={formatPaymentMethodLabel(data?.paymentMethod)} />
            <SubmittedDetailRow label="Reference" value={data?.paymentReferenceNumber} mono />
            <SubmittedDetailRow label="Payment date" value={formatPaymentDate(data?.paymentDate)} />
            <SubmittedDetailRow
              label="Amount"
              value={money(data?.paymentAmount || data?.invoice?.grandTotal || data?.taxInvoice?.grandTotal, data?.pricingMarket)}
            />
            {Number(data?.creditAmountApplied || 0) > 0 && (
              <SubmittedDetailRow
                label="Credit applied"
                value={money(data.creditAmountApplied, data?.pricingMarket)}
              />
            )}
            {Number(data?.excessAmount || 0) > 0 && (
              <SubmittedDetailRow
                label="Excess payment"
                value={`${money(data.excessAmount, data?.pricingMarket)} (to be credited upon approval)`}
              />
            )}
          </dl>
          {receipts.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Receipts uploaded</p>
              <ul className="mt-1 space-y-1">
                {receipts.map((r, idx) => (
                  <li key={r.fileId || `${r.fileName}-${idx}`} className="text-sm text-amber-900 flex items-center gap-2">
                    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="truncate">{r.originalName || r.fileName || 'Receipt'}</span>
                    {r.fileSize ? (
                      <span className="text-amber-800/70 text-xs whitespace-nowrap">({bytesToLabel(r.fileSize)})</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="mt-4 text-xs text-amber-900/80">
            You can safely close this page now. If approval is taking longer than expected, contact the event organiser.
          </p>
        </div>
      </div>
    </div>
  );
};

export const PaymentRejectedCard = ({ data }) => (
  <div className="bg-red-50 border-l-4 border-red-500 border border-red-200 rounded-xl shadow-sm p-4 sm:p-5">
    <div className="flex items-start gap-3">
      <div className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-red-100 text-red-700">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="text-base sm:text-lg font-semibold text-red-900">Previous payment was rejected</h2>
        {data?.rejectionReason ? (
          <p className="mt-1 text-sm text-red-900/90 whitespace-pre-wrap">
            <span className="font-semibold">Reason:</span> {data.rejectionReason}
          </p>
        ) : (
          <p className="mt-1 text-sm text-red-900/90">
            Your previous payment submission was rejected by the event organiser. Please re-submit using the form below.
          </p>
        )}
        {data?.rejectedAt ? (
          <p className="mt-1 text-xs text-red-900/70">Rejected on {formatPaymentDate(data.rejectedAt)}</p>
        ) : null}
        <p className="mt-3 text-sm text-red-900/90">
          You can submit a corrected payment using the form below.
        </p>
      </div>
    </div>
  </div>
);

export const PublicPaymentPageLoading = () => (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 p-6 flex items-center justify-center">
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm w-full max-w-md">
      <EventLoadingState message="Loading payment details..." />
    </div>
  </div>
);

export const PublicPaymentPageError = ({ message }) => (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 p-6 flex items-center justify-center">
    <div className="bg-white border border-red-200 rounded-xl px-6 py-5 shadow-sm max-w-md text-red-700 space-y-2">
      <p className="font-medium">Payment link is invalid or could not be loaded.</p>
      {message ? <p className="text-sm text-red-600/90 whitespace-pre-wrap">{message}</p> : null}
    </div>
  </div>
);

export const EventPaymentTermsModal = ({
  open,
  onClose,
  eventTitle,
  /** When set, used as the full modal heading (ignores eventTitle suffix). */
  modalTitle,
  paymentAgreementText,
  /** When true, show only paymentAgreementText from event settings — no built-in default list. */
  useEventSettingsOnly = false,
}) => {
  if (!open) return null;
  const heading = modalTitle
    || `${eventTitle || 'SLIM Event'} - Payment Terms & Conditions`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} role="presentation" />
      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-xl border border-gray-200 max-h-[80vh] overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">
            {heading}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-lg leading-none"
            aria-label="Close terms and conditions"
          >
            ×
          </button>
        </div>
        <div className="px-4 sm:px-5 py-4 overflow-y-auto text-sm text-gray-700">
          {paymentAgreementText ? (
            <p className="whitespace-pre-wrap leading-relaxed">{paymentAgreementText}</p>
          ) : useEventSettingsOnly ? (
            <p className="text-gray-600">
              Payment agreement content has not been configured in event settings. Please contact the event team.
            </p>
          ) : (
            <ol className="ml-5 list-decimal space-y-1.5">
              {defaultPaymentTerms.map((term, index) => (
                <li key={index}>{term}</li>
              ))}
            </ol>
          )}
        </div>
        <div className="px-4 sm:px-5 py-3 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const publicPaymentPageShellClass = 'min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 py-6 px-3 sm:px-6';
export const publicPaymentPageContainerClass = 'max-w-6xl mx-auto space-y-4';

export const publicPaymentHeaderProps = {
  logoAlt: 'SLIM logo',
  logoClassName: 'h-11 w-auto object-contain',
  rightLogoClassName: 'h-14 w-14 rounded-full object-contain border border-amber-200 bg-white p-1',
};
